import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/pages/AuthCallback';
import LandlordDashboard from '@/pages/LandlordDashboard';
import TenantDashboard from '@/pages/TenantDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import PropertyDetailPage from '@/pages/PropertyDetailPage';
import LeasesPage from '@/pages/LeasesPage';
import LeaseDetailPage from '@/pages/LeaseDetailPage';
import MessagesPage from '@/pages/MessagesPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';
import FormsPage from '@/pages/FormsPage';
import LandlordEarningsPage from '@/pages/LandlordEarningsPage';
import MediaLibraryPage from '@/pages/MediaLibraryPage';
import EmployeesPage from '@/pages/EmployeesPage';
import InviteAcceptPage from '@/pages/InviteAcceptPage';
import PayoutsPage from '@/pages/PayoutsPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ComparePage from '@/pages/ComparePage';
import { ViewportProvider } from '@/context/ViewportContext';
import { ReportBugFloating } from '@/components/ReportBug';
import { CompareTray } from '@/components/CompareTray';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { PwaInstallPrompt } from '@/components/PwaInstallPrompt';
import { PushNotificationsPrompt } from '@/components/PushNotificationsPrompt';
import { log } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send httpOnly cookies with all requests so the server can authenticate us via cookie.
axios.defaults.withCredentials = true;

export const AuthContext = React.createContext();

function AppRouter({ user, isAuthed }) {
  const location = useLocation();
  // Handle Google OAuth callback: session_id in URL fragment
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/landlord/dashboard" element={user?.role === 'landlord' || user?.role === 'employee' ? <LandlordDashboard /> : <Navigate to="/auth" />} />
      <Route path="/landlord/earnings" element={user?.role === 'landlord' || user?.role === 'employee' ? <LandlordEarningsPage /> : <Navigate to="/auth" />} />
      <Route path="/landlord/employees" element={user?.role === 'landlord' ? <EmployeesPage /> : <Navigate to="/auth" />} />
      <Route path="/landlord/payouts" element={user?.role === 'landlord' ? <PayoutsPage /> : <Navigate to="/auth" />} />
      <Route path="/invite/accept" element={<InviteAcceptPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/tenant/dashboard" element={user?.role === 'tenant' ? <TenantDashboard /> : <Navigate to="/auth" />} />
      <Route path="/admin/dashboard" element={user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/auth" />} />
      <Route path="/property/:id" element={<PropertyDetailPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/leases" element={isAuthed ? <LeasesPage /> : <Navigate to="/auth" />} />
      <Route path="/leases/:id" element={isAuthed ? <LeaseDetailPage /> : <Navigate to="/auth" />} />
      <Route path="/messages" element={isAuthed ? <MessagesPage /> : <Navigate to="/auth" />} />
      <Route path="/forms" element={isAuthed ? <FormsPage /> : <Navigate to="/auth" />} />
      <Route path="/media" element={isAuthed ? <MediaLibraryPage /> : <Navigate to="/auth" />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  // The real JWT lives only in an httpOnly cookie. We keep a truthy marker in memory
  // so existing `Authorization: Bearer ${token}` headers remain non-empty; the backend
  // prefers the cookie for authentication.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setToken('session-active');
    } catch (error) {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = useCallback((_newToken, newUser) => {
    // Server has already set the httpOnly cookie in the login response.
    // We never persist the raw JWT client-side (defense against XSS token theft).
    setToken('session-active');
    setUser(newUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Even if the network call fails, we clear local state so the user is logged out client-side.
      log.debug('logout: server call failed (clearing local state anyway)', e);
    }
    setToken(null);
    setUser(null);
  }, []);

  const authValue = useMemo(
    () => ({ user, token, login, logout }),
    [user, token, login, logout]
  );

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>;

  return (
    <AuthContext.Provider value={authValue}>
      <ViewportProvider>
        <BrowserRouter>
          <AppRouter user={user} isAuthed={!!user} />
          <MobileBottomNav />
          <CompareTray />
          <PwaInstallPrompt />
          <PushNotificationsPrompt />
          <ReportBugFloating />
        </BrowserRouter>
        <Toaster position="top-right" />
      </ViewportProvider>
    </AuthContext.Provider>
  );
}

export default App;
